import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate, convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './ProgressReportsTab.scss';

const ProgressReportsTab = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [migrated, setMigrated] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    requiresMigration: 0,
    migrated: 0,
    failed: 0
  });
  const [initialized, setInitialized] = useState(false);

  // Define timestamp fields to convert
  const timestampFields = ['submittedAt', 'reviewedAt', 'weekStartDate', 'weekEndDate'];

  // Fetch progress reports from Firestore
  const fetchProgressReports = async () => {
    // Prevent multiple fetches if already loading
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const progressReportsRef = collection(db, 'progressReports');
      const querySnapshot = await getDocs(progressReportsRef);
      
      const progressReportsData = [];
      querySnapshot.forEach((doc) => {
        // Convert timestamps to Date objects
        const data = convertObjectDates(doc.data(), timestampFields);
        
        progressReportsData.push({
          id: doc.id,
          ...data,
        });
      });
      
      // Process the data to identify which reports need migration
      const processed = progressReportsData.map(report => {
        // Check if the user email needs migration
        const needsEmailMigration = emailChanges.find(change => 
          change.oldEmail === report.email
        );
        
        // Check if the reviewer email needs migration
        const needsReviewerEmailMigration = report.reviewer && report.reviewer.email && 
          emailChanges.find(change => change.oldEmail === report.reviewer.email);
        
        return {
          ...report,
          requiresMigration: Boolean(needsEmailMigration || needsReviewerEmailMigration),
          userEmailChange: needsEmailMigration,
          reviewerEmailChange: needsReviewerEmailMigration,
          migrated: false,
          error: null
        };
      });
      
      // Update stats
      setStats({
        total: processed.length,
        requiresMigration: processed.filter(report => report.requiresMigration).length,
        migrated: 0,
        failed: 0
      });
      
      setReports(processed);
      setInitialized(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching progress reports:', error);
      setError('Failed to fetch progress reports: ' + error.message);
      setInitialized(true);
      setLoading(false);
    }
  };

  // Migrate a single progress report
  const migrateProgressReport = async (report) => {
    try {
      const reportRef = doc(db, 'progressReports', report.id);
      const updateData = {};

      // Update user email if needed
      if (report.userEmailChange) {
        updateData.email = report.userEmailChange.newEmail;
      }

      // Update reviewer email if needed
      if (report.reviewerEmailChange && report.reviewer) {
        updateData['reviewer.email'] = report.reviewerEmailChange.newEmail;
      }

      // Only update if there are changes to make
      if (Object.keys(updateData).length > 0) {
        await updateDoc(reportRef, updateData);
        return { success: true };
      } else {
        return { success: true, noChangesNeeded: true };
      }
    } catch (error) {
      console.error(`Error migrating progress report ${report.id}:`, error);
      return { success: false, error: error.message };
    }
  };

  // Migrate all reports that require migration
  const migrateAllReports = async () => {
    if (!reports || reports.length === 0) return;
    
    setLoading(true);
    const updatedReports = [...reports];
    let migratedCount = 0;
    let failedCount = 0;
    
    for (let i = 0; i < updatedReports.length; i++) {
      const report = updatedReports[i];
      
      if (report.requiresMigration && !report.migrated) {
        const result = await migrateProgressReport(report);
        
        updatedReports[i] = {
          ...report,
          migrated: result.success,
          error: result.success ? null : result.error
        };
        
        if (result.success) {
          migratedCount++;
        } else {
          failedCount++;
        }
      }
    }
    
    // Update stats
    setStats(prev => ({
      ...prev,
      migrated: prev.migrated + migratedCount,
      failed: prev.failed + failedCount
    }));
    
    setReports(updatedReports);
    setMigrated(updatedReports.filter(report => report.migrated));
    setLoading(false);
  };

  // Format date timestamp for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestampToDate(timestamp);
    return date ? date.toLocaleString() : 'Invalid Date';
  };

  // Truncate long text for display
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Only run fetchProgressReports once on component mount
  useEffect(() => {
    if (!initialized && !loading) {
      fetchProgressReports();
    }
  }, [initialized, loading]);

  return (
    <div className="progress-reports-tab tab-content-container">
      <div className="tab-header">
        <h2>Progress Reports Migration</h2>
        <p>Migrate email addresses in progress reports from myelinh.com to myelinz.com domain.</p>
      </div>
      
      <div className="migration-stats">
        <div className="stat-item">
          <span className="stat-label">Total Reports:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Requires Migration:</span>
          <span className="stat-value">{stats.requiresMigration}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Migrated:</span>
          <span className="stat-value">{stats.migrated}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Failed:</span>
          <span className="stat-value">{stats.failed}</span>
        </div>
      </div>
      
      <div className="migration-actions">
        <button 
          className="fetch-btn" 
          onClick={fetchProgressReports} 
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh Data'}
        </button>
        <button 
          className="migrate-btn" 
          onClick={migrateAllReports} 
          disabled={loading || stats.requiresMigration === 0 || stats.migrated === stats.requiresMigration}
        >
          {loading ? 'Migrating...' : 'Migrate All'}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="data-table-container">
        <h3>Progress Reports Requiring Migration</h3>
        {loading ? (
          <div className="loading-indicator">Loading...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Current User Email</th>
                <th>New User Email</th>
                <th>Current Reviewer Email</th>
                <th>New Reviewer Email</th>
                <th>Submitted At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reports.filter(report => report.requiresMigration).map(report => (
                <tr key={report.id} className={report.migrated ? 'migrated' : (report.error ? 'error' : '')}>
                  <td title={report.id}>{truncateText(report.id, 10)}</td>
                  <td title={report.email}>{report.email}</td>
                  <td title={report.userEmailChange?.newEmail || 'No change'}>
                    {report.userEmailChange ? report.userEmailChange.newEmail : 'No change'}
                  </td>
                  <td title={report.reviewer?.email || 'N/A'}>
                    {report.reviewer?.email || 'N/A'}
                  </td>
                  <td title={report.reviewerEmailChange?.newEmail || 'No change'}>
                    {report.reviewerEmailChange ? report.reviewerEmailChange.newEmail : 'No change'}
                  </td>
                  <td>{formatDate(report.submittedAt)}</td>
                  <td>
                    {report.migrated ? (
                      <span className="status-success">Migrated</span>
                    ) : report.error ? (
                      <span className="status-error" title={report.error}>Failed</span>
                    ) : (
                      <span className="status-pending">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
              {reports.filter(report => report.requiresMigration).length === 0 && (
                <tr>
                  <td colSpan="7" className="no-data">No progress reports require migration</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      {migrated.length > 0 && (
        <div className="migration-results">
          <h3>Successfully Migrated Reports</h3>
          <div className="results-count">Total: {migrated.length}</div>
          <ul className="results-list">
            {migrated.map(report => (
              <li key={report.id}>
                {report.email} → {report.userEmailChange?.newEmail || report.email}
                {report.reviewerEmailChange && (
                  <span className="reviewer-email">
                    [Reviewer: {report.reviewer?.email} → {report.reviewerEmailChange?.newEmail}]
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProgressReportsTab; 